import { useState } from "react";
import { Zap, Loader2, Check } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

const TIERS = [
  {
    id: "tier_5",
    credits: 5,
    price: "€4,99",
    label: "Starter",
    perToken: "€1,00 / token",
  },
  {
    id: "tier_10",
    credits: 10,
    price: "€7,99",
    label: "Populair",
    perToken: "€0,80 / token",
    highlight: true,
  },
  {
    id: "tier_20",
    credits: 20,
    price: "€14,99",
    label: "Voordelig",
    perToken: "€0,75 / token",
  },
];

const BuyCreditsDialog = ({ open, onOpenChange, buyCredits, isDark }) => {
  const [loading, setLoading] = useState(null);
  const [err, setErr] = useState(null);

  const handleBuy = async (tierId) => {
    setLoading(tierId);
    setErr(null);
    try {
      const { checkoutUrl } = await buyCredits(tierId);
      if (checkoutUrl) window.location.href = checkoutUrl;
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Betaling starten mislukt");
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            Extra AI-tokens kopen
          </DialogTitle>
          <DialogDescription>
            Je hebt 3 gratis AI-verzoeken per dag. Tokens worden gebruikt voor preview
            scores, inzichten, hooks en scripts genereren.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 my-1">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              className={cn(
                "relative rounded-xl border p-4 flex flex-col gap-2 transition-colors",
                tier.highlight
                  ? isDark
                    ? "bg-indigo-500/10 border-indigo-500/30"
                    : "bg-indigo-50 border-indigo-200"
                  : isDark
                    ? "bg-white/[0.02] border-white/[0.08]"
                    : "bg-white border-gray-200"
              )}
            >
              {tier.highlight && (
                <span
                  className={cn(
                    "absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold px-2 py-0.5 rounded-full",
                    isDark
                      ? "bg-indigo-500 text-white"
                      : "bg-indigo-600 text-white"
                  )}
                >
                  Populair
                </span>
              )}
              <div className={cn("text-[11px] font-semibold", isDark ? "text-gray-400" : "text-gray-500")}>
                {tier.label}
              </div>
              <div className={cn("text-[28px] font-bold leading-none", isDark ? "text-white" : "text-gray-900")}>
                {tier.credits}
                <span className={cn("text-[13px] font-normal ml-1", isDark ? "text-gray-400" : "text-gray-500")}>tokens</span>
              </div>
              <div className={cn("text-[18px] font-semibold", isDark ? "text-indigo-300" : "text-indigo-700")}>
                {tier.price}
              </div>
              <div className={cn("text-[10px]", isDark ? "text-gray-500" : "text-gray-400")}>
                {tier.perToken}
              </div>
              <Button
                size="sm"
                onClick={() => handleBuy(tier.id)}
                disabled={!!loading}
                className={cn(
                  "mt-1 w-full",
                  tier.highlight ? "" : "variant-outline"
                )}
                variant={tier.highlight ? "default" : "outline"}
              >
                {loading === tier.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  "Kopen"
                )}
              </Button>
            </div>
          ))}
        </div>

        {err && (
          <p className={cn("text-[12px] text-center", isDark ? "text-red-400" : "text-red-600")}>
            {err}
          </p>
        )}

        <p className={cn("text-[11px] text-center", isDark ? "text-gray-600" : "text-gray-400")}>
          Betaling via Mollie · Veilig · Eenmalig
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default BuyCreditsDialog;
